import React, { useEffect, useState, useRef } from 'react';

/**
 * Global Flash Overlay - listens for window events:
 *   window.dispatchEvent(new CustomEvent('flash:success', { detail: { message } }))
 *   window.dispatchEvent(new CustomEvent('flash:error',   { detail: { message } }))
 *
 * Success: animated green checkmark, 2s auto-dismiss.
 * Error:   animated red "X" with reason text below, 2.5s auto-dismiss.
 */
const FlashOverlay = () => {
  const [state, setState] = useState(null); // { kind: 'success'|'error', message, key }
  const timerRef = useRef(null);

  useEffect(() => {
    const onSuccess = (e) => {
      const msg = e.detail?.message || 'ΕΠΙΤΥΧΙΑ';
      setState({ kind: 'success', message: msg, key: Date.now() });
    };
    const onError = (e) => {
      const msg = e.detail?.message || 'ΣΦΑΛΜΑ';
      setState({ kind: 'error', message: msg, key: Date.now() });
    };
    window.addEventListener('flash:success', onSuccess);
    window.addEventListener('flash:error', onError);
    return () => {
      window.removeEventListener('flash:success', onSuccess);
      window.removeEventListener('flash:error', onError);
    };
  }, []);

  useEffect(() => {
    if (!state) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    const dur = state.kind === 'success' ? 2000 : 2500;
    timerRef.current = setTimeout(() => setState(null), dur);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [state]);

  if (!state) return null;

  const isOk = state.kind === 'success';
  const color = isOk ? '#16a34a' : '#dc2626';

  return (
    <div
      key={state.key}
      data-testid={isOk ? 'flash-success' : 'flash-error'}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 backdrop-blur-sm pointer-events-none flash-fade-in"
    >
      <div className="bg-white rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center gap-3 flash-pop max-w-[90vw]">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52" className="w-24 h-24">
          <circle cx="26" cy="26" r="24" fill="none" stroke={color} strokeWidth="3" className="flash-circle" />
          {isOk ? (
            <path d="M14 27 L23 36 L40 18" fill="none" stroke={color} strokeWidth="4"
                  strokeLinecap="round" strokeLinejoin="round" className="flash-check" />
          ) : (
            <>
              <path d="M17 17 L35 35" fill="none" stroke={color} strokeWidth="4"
                    strokeLinecap="round" className="flash-x1" />
              <path d="M35 17 L17 35" fill="none" stroke={color} strokeWidth="4"
                    strokeLinecap="round" className="flash-x2" />
            </>
          )}
        </svg>
        <div className={`text-lg font-bold text-center ${isOk ? 'text-green-700' : 'text-red-700'}`}>
          {isOk ? 'ΕΠΙΤΥΧΙΑ' : 'ΣΦΑΛΜΑ'}
        </div>
        <div className="text-sm text-gray-700 text-center max-w-xs whitespace-pre-line">
          {state.message}
        </div>
      </div>

      <style>{`
        @keyframes flash-pop { 0% { transform: scale(0.7); opacity: 0; } 60% { transform: scale(1.05); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
        @keyframes flash-fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes flash-draw-circle { from { stroke-dashoffset: 160; } to { stroke-dashoffset: 0; } }
        @keyframes flash-draw-check { from { stroke-dashoffset: 60; } to { stroke-dashoffset: 0; } }
        @keyframes flash-draw-x { from { stroke-dashoffset: 30; } to { stroke-dashoffset: 0; } }
        .flash-pop { animation: flash-pop 0.35s cubic-bezier(0.2, 0.9, 0.3, 1.2) both; }
        .flash-fade-in { animation: flash-fade-in 0.18s ease-out both; }
        .flash-circle { stroke-dasharray: 160; stroke-dashoffset: 160; animation: flash-draw-circle 0.45s ease-out 0.05s forwards; }
        .flash-check  { stroke-dasharray: 60;  stroke-dashoffset: 60;  animation: flash-draw-check 0.30s ease-out 0.45s forwards; }
        .flash-x1     { stroke-dasharray: 30;  stroke-dashoffset: 30;  animation: flash-draw-x 0.22s ease-out 0.40s forwards; }
        .flash-x2     { stroke-dasharray: 30;  stroke-dashoffset: 30;  animation: flash-draw-x 0.22s ease-out 0.62s forwards; }
      `}</style>
    </div>
  );
};

/** Helpers callable from anywhere */
export const flashSuccess = (message) => {
  window.dispatchEvent(new CustomEvent('flash:success', { detail: { message } }));
};
export const flashError = (message) => {
  window.dispatchEvent(new CustomEvent('flash:error', { detail: { message } }));
};

export default FlashOverlay;
